import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Linkedin, MapPin, ArrowUpRight, Download, ShieldCheck } from "lucide-react";
import { PROFILE } from "./data";
import ResumeModal from "../resume/ResumeModal";

const Contact = () => {
  const [resumeOpen, setResumeOpen] = React.useState(false);
  return (
    <section id="contact" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-[32px] border border-white/10 bg-gradient-to-br from-white/[0.06] via-white/[0.02] to-transparent backdrop-blur-2xl overflow-hidden p-10 md:p-20"
        >
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#e6c769]/15 blur-[120px]" />
          <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#3b82f6]/15 blur-[120px]" />

          <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-12 items-end">
            <div>
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">10 / Get in touch</div>
              <h2 className="mt-4 text-[44px] md:text-[88px] leading-[0.95] tracking-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Let’s build<br />
                <span className="italic">something serious</span>.
              </h2>
              <p className="mt-6 max-w-xl text-[16px] leading-[1.75] text-white/60">
                I write back to thoughtful people — founders, investors, students, mentors. If our work might intersect, I’d love to hear from you.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <a
                  href={`mailto:${PROFILE.email}`}
                  className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-[#e6c769] text-black font-medium text-[14px] tracking-wide hover:bg-white transition-colors"
                >
                  <Mail className="w-4 h-4" /> {PROFILE.email}
                  <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href={PROFILE.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/[0.06] hover:border-white/30 transition-colors text-[14px]"
                >
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
                <button
                  type="button"
                  onClick={() => setResumeOpen(true)}
                  data-testid="contact-resume-btn"
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/[0.06] hover:border-white/30 transition-colors text-[14px]"
                >
                  <Download className="w-4 h-4" /> Download Resume
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-4 lg:items-end">
              <div className="text-[11px] tracking-[0.4em] uppercase text-white/40 font-mono">Based in</div>
              <div className="flex items-center gap-2 text-white/80">
                <MapPin className="w-4 h-4 text-[#e6c769]" />
                <span className="text-[20px]" style={{ fontFamily: "'Playfair Display', serif" }}>{PROFILE.city}</span>
              </div>
              <div className="mt-8 text-[11px] tracking-[0.4em] uppercase text-white/40 font-mono">Open for</div>
              <ul className="flex flex-col gap-1 text-white/70 text-[14px]">
                <li>Investment conversations</li>
                <li>Strategic collaborations</li>
                <li>Mentorship &amp; learning</li>
                <li>Founder coffee chats</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-[12px] text-white/40 font-mono">© {new Date().getFullYear()} Utkarsh Bokadia · Personal Archive</div>
          <div className="flex items-center gap-6">
            <div className="text-[12px] text-white/40 font-mono tracking-wider">Designed &amp; built with care.</div>
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase font-mono text-white/35 hover:text-[#e6c769] transition-colors"
            >
              <ShieldCheck className="w-3 h-3" />
              Admin
            </Link>
          </div>
        </footer>
      </div>

      <ResumeModal open={resumeOpen} onClose={() => setResumeOpen(false)} />
    </section>
  );
};

export default Contact;
